import {
  Activity,
  Boxes,
  ClipboardList,
  Cpu,
  Layers,
  MonitorSmartphone,
  Timer,
  Workflow,
} from "lucide-react";
import { Section, SectionHeader, Card, CapabilityCard } from "@/components/ds";
import { PLATFORM_URL } from "@/lib/navigation";

/** Credibility introduction — who PathFree is and what is being built. */
export const CredibilityIntro = () => (
  <Section tone="mist" aria-labelledby="credibility-heading">
    <div className="reveal grid gap-10 lg:grid-cols-12 lg:gap-14">
      <div className="lg:col-span-6">
        <p className="eyebrow eyebrow-dot mb-3">Who we are</p>
        <h2 id="credibility-heading" className="ds-h2">
          PathFree is developing an integrated approach to resuscitation.
        </h2>
        <p className="ds-lead mt-4">
          Readiness, response, documentation, and improvement are treated as one
          continuous system rather than a set of disconnected tools. datasyteAI™ provides
          the intelligence and workflow layer; AiMediQ™ brings it to the bedside.
        </p>
        <p className="ds-body mt-4 text-muted-foreground">
          PathFree Technologies Corporation is a medical-technology company in the
          development and validation phase. Everything shown on this site is described
          in terms of current capability or active development — never as a cleared or
          commercially deployed product.
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:col-span-6">
        {[
          {
            icon: Layers,
            title: "One connected system",
            body: "Hardware, software, workflow, and analytics designed together.",
          },
          {
            icon: ClipboardList,
            title: "Clinician-guided design",
            body: "Built with resuscitation practitioners and institutional protocols in mind.",
          },
          {
            icon: Activity,
            title: "Evidence-first posture",
            body: "Claims tied to documented development milestones and validation work.",
          },
          {
            icon: Cpu,
            title: "Enterprise-ready intent",
            body: "Integration, security, and governance considered from the start.",
          },
        ].map(({ icon: Icon, title, body }) => (
          <Card key={title} className="flex h-full flex-col gap-2">
            <span className="inline-flex size-10 items-center justify-center rounded-lg border border-border bg-surface text-primary">
              <Icon className="size-5" />
            </span>
            <h3 className="ds-h5">{title}</h3>
            <p className="ds-caption">{body}</p>
          </Card>
        ))}
      </div>
    </div>
  </Section>
);

const problems = [
  {
    icon: Cpu,
    title: "Cognitive load at peak pressure",
    body: "Teams recall protocol steps, dosing, and intervals while managing a deteriorating patient.",
  },
  {
    icon: Boxes,
    title: "Fragmented tools",
    body: "Monitors, carts, paper forms, whiteboards, and the record rarely operate as one workflow.",
  },
  {
    icon: ClipboardList,
    title: "Manual documentation",
    body: "Events are frequently reconstructed afterward from memory and partial notes.",
  },
  {
    icon: Timer,
    title: "Inconsistent timestamps",
    body: "Different clocks and recorders make intervals difficult to compare across events.",
  },
  {
    icon: Boxes,
    title: "Supply readiness",
    body: "Cart contents, expirations, and restock status are typically checked outside the digital workflow.",
  },
  {
    icon: Workflow,
    title: "Post-event data fragmentation",
    body: "Review depends on assembling data from systems that were never designed to align.",
  },
];

/** Problem statement — qualitative only, no unsupported numeric claims. */
export const ProblemStatement = () => (
  <Section aria-labelledby="problem-heading">
    <SectionHeader
      eyebrow="The problem"
      title={
        <span id="problem-heading">
          Resuscitation is a team event. The technology should work as one system.
        </span>
      }
      description="Today the burden of coordination falls on the people in the room. These are the friction points clinicians and quality leaders describe most often."
      className="reveal"
    />
    <div className="reveal mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {problems.map(({ icon, title, body }) => (
        <CapabilityCard key={title} icon={icon} title={title}>
          {body}
        </CapabilityCard>
      ))}
    </div>
  </Section>
);

/** Two-part platform — intelligence layer plus the connected clinical system. */
export const TwoPartPlatform = () => (
  <Section tone="mist" aria-labelledby="platform-heading">
    <SectionHeader
      eyebrow="The platform"
      title={<span id="platform-heading">Two parts, designed as one platform</span>}
      description="An intelligence layer and a connected clinical system that share the same event, the same timeline, and the same record."
      className="reveal"
    />
    <div className="reveal mt-10 grid gap-6 lg:grid-cols-2">
      {[
        {
          icon: Cpu,
          name: "datasyteAI™",
          role: "The intelligence layer",
          summary:
            "Decision support, workflow orchestration, documentation, integration, and analytics — modeled on institutional protocols and expert practice.",
          bullets: [
            "Real-time next-step, timing, and dosing prompts",
            "Structured event capture and intelligent documentation",
            "Hospital-system and device integration pathways",
            "Post-event analytics for quality and safety review",
          ],
          href: "/datasyteai",
        },
        {
          icon: MonitorSmartphone,
          name: "AiMediQ™",
          role: "The connected clinical system",
          summary:
            "The point-of-care system that carries that intelligence into the room, keeping the team, the supplies, and the record aligned.",
          bullets: [
            "Shared event timeline visible to the whole team",
            "Role and task orientation from activation",
            "Inventory and readiness awareness",
            "Designed for training and simulation reuse",
          ],
          href: "/aimediq",
        },
      ].map(({ icon: Icon, name, role, summary, bullets, href }) => (
        <Card key={name} interactive className="flex h-full flex-col gap-4">
          <span className="inline-flex size-11 items-center justify-center rounded-lg border border-border bg-surface text-primary">
            <Icon className="size-5" />
          </span>
          <div>
            <p className="ds-label text-muted-foreground">{role}</p>
            <h3 className="ds-h3 mt-1">{name}</h3>
          </div>
          <p className="ds-body text-muted-foreground">{summary}</p>
          <ul className="space-y-2">
            {bullets.map((b) => (
              <li key={b} className="ds-caption flex gap-2">
                <span aria-hidden className="mt-[0.45rem] size-1.5 shrink-0 rounded-full bg-signal" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <a href={href} className="ds-nav-link mt-auto pt-2 text-primary">
            See what {name} does →
          </a>
        </Card>
      ))}
    </div>
    <p className="ds-caption reveal mt-6">
      Prefer the full picture first?{" "}
      <a href={PLATFORM_URL} className="ds-link">
        Explore the platform overview
      </a>
      .
    </p>
  </Section>
);
